import type { ReactNode } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { PageShell, Section } from '~/components/layout';
import { ButtonLink } from '~/components/ui';
import {
  businessApplyHref,
  businessCallHref,
  businessPayrollTermsHref,
  businessWaitlistHref,
} from '~/content/business';
import type { LinkItem } from '~/content/types';
import { pageHeadMeta } from '~/lib/seo';

export const Route = createFileRoute('/business_/how-it-works')({
  head: () =>
    pageHeadMeta({
      title: 'How nsave Payroll Works',
      description:
        'Pay your international team from one nsave Business account. Salaries land instantly and free in employee nsave accounts, or within hours in their local bank account.',
      path: '/business/how-it-works',
    }),
  component: BusinessHowItWorksPage,
});

const payOptions = [
  { label: 'Speed', nsave: 'Instant', bank: 'Within hours' },
  { label: 'nsave fee', nsave: 'Free', bank: 'Free for salaries above $400' },
  {
    label: 'What your employee needs',
    nsave: 'A free nsave account',
    bank: 'Their local bank details',
  },
  {
    label: 'Best for',
    nsave: 'Teams who want their salary fast, in USD, with a card and local cash-out',
    bank: 'Employees who want their pay to land in the bank they already use (local currency)',
  },
];

const steps: { title: string; body: ReactNode }[] = [
  {
    title: '1. Apply for a payroll account',
    body: (
      <>
        <p>
          Apply for an nsave Business payroll account. The $200 application fee means our team can
          focus on businesses with real payroll needs and give each one proper onboarding.
        </p>
        <p>
          Once you’re approved, you get an international account for paying employees and
          contractors in every country you pay into.
        </p>
      </>
    ),
  },
  {
    title: '2. Fund your account',
    body: (
      <p>
        Add money to your nsave Business account in USD, EUR or GBP at no cost. You can also use the
        account to receive payments from your clients, so revenue and payroll can sit in the same
        place.
      </p>
    ),
  },
  {
    title: '3. Add your team',
    body: (
      <>
        <p>
          Each employee or contractor needs a personal nsave account, which they can open for free
          in the nsave app. You then pay them using their email address or nsave tag to process bulk
          transfers in one click. There are no bank details to collect and no SWIFT codes to check.
          Our system automatically pulls them from just their emails or ntags.
        </p>
        <p>
          Your company can be based in the US, UK, the GCC or elsewhere, and your team can be in
          Pakistan, Egypt and other supported markets.
        </p>
      </>
    ),
  },
  {
    title: '4. Run payroll',
    body: (
      <>
        <p>
          Send salaries to your whole team in one bulk payment instead of setting up a separate
          international wire for each person.
        </p>
        <p>Every transfer from your nsave Business account to an employee’s nsave account is:</p>
        <ul className="list-disc space-y-4 ps-5">
          <li>
            <strong className="font-semibold text-ink">Instant.</strong> No waiting days for a wire
            to clear.
          </li>
          <li>
            <strong className="font-semibold text-ink">Free.</strong> nsave charges no fee on
            payroll payments between nsave accounts.
          </li>
          <li>
            <strong className="font-semibold text-ink">Predictable.</strong> Your team knows when
            their salary will land, so you get fewer “has it arrived yet?” messages.
          </li>
        </ul>
      </>
    ),
  },
  {
    title: '5. Your team decides what to do with their salary',
    body: (
      <>
        <p>
          Once the salary lands, your employees choose how to use it. Depending on their country and
          eligibility, they can:
        </p>
        <ul className="list-disc space-y-4 ps-5">
          <li>
            <strong className="font-semibold text-ink">Hold USD</strong> instead of converting
            everything on payday
          </li>
          <li>
            <strong className="font-semibold text-ink">Spend worldwide</strong> with a free Visa
            Platinum card
          </li>
          <li>
            <strong className="font-semibold text-ink">Cash out locally</strong> to supported banks
            and wallets, whenever they want
          </li>
          <li>
            <strong className="font-semibold text-ink">Convert currency</strong> at competitive
            rates
          </li>
          <li>
            <strong className="font-semibold text-ink">Invest</strong> through supported investment
            features
          </li>
        </ul>
      </>
    ),
  },
];

function BusinessHowItWorksPage() {
  return (
    <PageShell>
      <Section className="pt-16 md:pt-24">
        <div className="mx-auto max-w-3xl">
          <h1 className="text-[40px] leading-[1.08] font-semibold tracking-[-0.03em] text-balance md:text-[60px]">
            How nsave Payroll works
          </h1>
          <p className="mt-9 text-h4 leading-snug font-semibold tracking-[-0.02em] text-ink">
            nsave Payroll lets you pay your international team from one business account. Salaries
            go either straight into each employee’s nsave account or to their local bank account
            instantly and at no cost.
          </p>
          <Prose className="mt-8">
            <p>
              Fund your nsave Business account once, then send salaries to your whole team in one
              bulk payment. There’s no monthly subscription and no need to set up a separate
              international wire for each person.
            </p>
          </Prose>

          <ArticleHeading>Two ways to pay your team</ArticleHeading>
          <div className="mt-8 overflow-x-auto rounded-[22px] border border-black-6">
            <table className="w-full min-w-[560px] text-start text-body-s">
              <thead className="bg-grey-50 text-ink">
                <tr>
                  <th scope="col" className="px-5 py-5 text-start" />
                  <th scope="col" className="px-5 py-5 text-start font-semibold">
                    To their nsave account
                  </th>
                  <th scope="col" className="px-5 py-5 text-start font-semibold">
                    To their local bank account
                  </th>
                </tr>
              </thead>
              <tbody>
                {payOptions.map((row) => (
                  <tr key={row.label} className="border-t border-black-6 align-top">
                    <th scope="row" className="px-5 py-5 text-start font-semibold text-ink">
                      {row.label}
                    </th>
                    <td className="px-5 py-5 text-grey-900">{row.nsave}</td>
                    <td className="px-5 py-5 text-grey-900">{row.bank}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Prose className="mt-8">
            <p>
              You can mix both in the same payroll run. Some of your team can be paid into nsave and
              the rest into their local banks.
            </p>
            <h3 className="pt-4 text-h5 font-semibold text-ink">
              Why many teams choose nsave accounts
            </h3>
            <p>
              Employees get paid instantly and free. They can hold USD, spend worldwide with a free
              Visa Platinum card, and cash out to a local bank or wallet whenever they want.
            </p>
            <h3 className="pt-4 text-h5 font-semibold text-ink">
              Example: paying an employee in Pakistan
            </h3>
            <p className="font-semibold text-ink">
              Your business account in the US (all countries supported) → nsave Business account →
              employee’s nsave account → Pakistani bank or wallet
            </p>
            <ol className="list-decimal space-y-4 ps-5">
              <li>You send the salary in USD from your nsave Business account.</li>
              <li>
                It lands in your employee’s nsave account in USD instantly, with no transfer fee, or
                you send it directly to your employee’s local Pakistani bank account in PKR.
              </li>
              <li>
                Your employee keeps their full salary, spends it with their card, or sends it to a
                supported Pakistani bank or wallet when they need it.
              </li>
            </ol>
          </Prose>
          <CtaRow
            primary={{ label: 'Apply for Payroll', href: businessApplyHref, external: true }}
            secondary={{ label: 'Request a Free Call', href: businessCallHref }}
          />

          <Divider />

          <ArticleHeading>What’s changing at nsave Business</ArticleHeading>
          <Prose className="mt-7">
            <p>
              We’re improving nsave Business, and until the end of the year we’re only opening new
              accounts for payroll use cases.
            </p>
            <ul className="list-disc space-y-4 ps-5">
              <li>
                <strong className="font-semibold text-ink">Already a customer?</strong> Nothing
                changes. Your account, and any application already in progress, works as normal.
              </li>
              <li>
                <strong className="font-semibold text-ink">
                  Need to pay an international team?
                </strong>{' '}
                Payroll accounts are open now. There’s a $200 application fee, refunded once your
                payroll through nsave passes $40,000.
              </li>
              <li>
                <strong className="font-semibold text-ink">
                  Need a business account for something else?
                </strong>{' '}
                General accounts reopen by the end of the year.{' '}
                <InlineLink href={businessWaitlistHref}>Join the waitlist</InlineLink> to hear
                first.
              </li>
            </ul>
          </Prose>

          <Divider />

          {steps.map((step) => (
            <div key={step.title}>
              <ArticleHeading>{step.title}</ArticleHeading>
              <Prose className="mt-7">{step.body}</Prose>
            </div>
          ))}

          <Divider />

          <ArticleHeading>The application fee and refund</ArticleHeading>
          <Prose className="mt-7">
            <ul className="list-disc space-y-4 ps-5">
              <li>
                <strong className="font-semibold text-ink">The fee:</strong> $200, paid when you
                apply.
              </li>
              <li>
                <strong className="font-semibold text-ink">The refund:</strong> we refund the full
                $200 once your total payroll transfers through nsave pass{' '}
                <strong className="font-semibold text-ink">$40,000</strong>.
              </li>
              <li>
                <strong className="font-semibold text-ink">What counts as payroll:</strong> payments
                from your business account to individuals (not businesses), sent in bulk, meaning
                more than 10 transfers to individuals in a calendar month.
              </li>
              <li>
                <strong className="font-semibold text-ink">Keeping your account:</strong> payroll
                accounts need to run at least one payroll payment every month.
              </li>
            </ul>
            <p>
              <InlineLink href={businessPayrollTermsHref}>Read the full Payroll Terms</InlineLink>
            </p>
          </Prose>

          <ArticleHeading>Can I use my payroll account for other things?</ArticleHeading>
          <Prose className="mt-7">
            <p>
              Yes. You can receive client payments and move funds as you would with any business
              account, as long as you keep running payroll every month.
            </p>
          </Prose>

          <ArticleHeading>Already have an nsave Business account?</ArticleHeading>
          <Prose className="mt-7">
            <p>
              Nothing changes for you. Existing accounts and applications already in progress are
              not affected.
            </p>
          </Prose>

          <ArticleHeading>Not looking for payroll?</ArticleHeading>
          <Prose className="mt-7">
            <p>
              We’re improving nsave Business and have paused new general business accounts until the
              end of the year.{' '}
              <InlineLink href={businessWaitlistHref}>Join the waitlist</InlineLink> and you’ll be
              among the first to hear when we reopen. Places are offered first come, first served.
            </p>
          </Prose>

          <Divider />

          <ArticleHeading>Not sure yet? Talk to us first</ArticleHeading>
          <Prose className="mt-7">
            <p>
              You don’t have to pay $200 to find out if nsave fits your company. Book a free call
              with our payroll team and tell us:
            </p>
            <ul className="list-disc space-y-4 ps-5">
              <li>where your company is based</li>
              <li>where your team is located</li>
              <li>how many people you pay</li>
              <li>how you run payroll today</li>
            </ul>
            <p>We’ll explain how nsave would work for you before you decide.</p>
          </Prose>
          <CtaRow
            secondary={{ label: 'Request a Free Payroll Call', href: businessCallHref }}
            note="No application fee required."
          />

          <ArticleHeading>Ready to start?</ArticleHeading>
          <CtaRow
            primary={{ label: 'Apply for Payroll: $200', href: businessApplyHref, external: true }}
            note={
              <>
                Refunded once your payroll through nsave passes $40,000.{' '}
                <InlineLink href={businessPayrollTermsHref}>T&Cs apply.</InlineLink>
              </>
            }
          />
        </div>
      </Section>
    </PageShell>
  );
}

function ArticleHeading({ children }: { children: ReactNode }) {
  return (
    <h2 className="mt-20 text-h4 font-semibold tracking-[-0.02em] text-balance md:text-h3">
      {children}
    </h2>
  );
}

function Prose({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div className={`space-y-6 text-body leading-loose text-grey-900 ${className ?? ''}`}>
      {children}
    </div>
  );
}

function Divider() {
  return <hr className="mt-20 border-black-6" />;
}

function InlineLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a
      href={href}
      className="font-semibold text-ink underline underline-offset-4 hover:text-red-500">
      {children}
    </a>
  );
}

function CtaRow({
  primary,
  secondary,
  note,
}: {
  primary?: LinkItem;
  secondary?: LinkItem;
  note?: ReactNode;
}) {
  return (
    <div className="mt-10 flex flex-wrap items-center gap-3">
      {primary && (
        <ButtonLink href={primary.href} external={primary.external} size="md">
          {primary.label}
        </ButtonLink>
      )}
      {secondary && (
        <ButtonLink href={secondary.href} external={secondary.external} variant="outline" size="md">
          {secondary.label}
        </ButtonLink>
      )}
      {note && <p className="text-body-s text-grey-600">{note}</p>}
    </div>
  );
}
